import { useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'

import CurrencyContext from 'contexts/currencyContext'
import GeoContext from 'contexts/geoContext'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import ImageCarousel from 'LuxKit/Carousel/ImageCarousel'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import Label from 'LuxKit/Label/Label'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import LineExternalLinkAltIcon from 'LuxKit/Icons/line/LineExternalLinkAltIcon'
import LineUserIcon from 'LuxKit/Icons/line/LineUserIcon'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import { addQueryParamsToPath } from 'lib/url/searchUrlUtils'
import { formatRoundedAmountWithDollarType } from 'lib/format/formatCurrencyIntl'
import AmenityItem from './AmenityItem'
import type { DrawerHotelRoomOption } from './types'

const RoomCard = styled.div`
  border: 1px solid ${(props) => props.theme.palette.neutral.default.five};
  border-radius: ${(props) => props.theme.borderRadius.S};
  overflow: hidden;
`

const RoomContent = styled.div`
  display: flex;
`

const RoomImageWrapper = styled.div`
  width: ${rem(280)};
  max-width: ${rem(350)};
  /* Stretch to match the right column's natural height so the image fills
     the full card. align-self: stretch gives the wrapper a definite cross-size
     from flex layout, which resolves ImageCarousel's internal height: 100%.
     min-height acts as a floor when the content column is unusually short. */
  align-self: stretch;
  min-height: ${rem(200)};
  flex-shrink: 0;
  overflow: hidden;
`

const RoomDetails = styled.div`
  flex: 1;
  min-width: 0;
  padding: ${rem(20)};
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
`

const AmenitiesGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${rem(8)} ${rem(16)};
`

const RoomFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${rem(8)};
  padding: ${rem(16)} ${rem(12)};
  border-top: 1px solid ${(props) => props.theme.palette.neutral.default.five};
  min-width: ${rem(80)};
`

const PackagesLink = styled(RoomFooter).attrs({ as: 'a' })`
  cursor: pointer;
  text-decoration: none;
  color: ${(props) => props.theme.palette.brand.primary.normal};
  font-weight: ${(props) => props.theme.font.secondary.weight.superBold};
  font-size: ${rem(14)};
  line-height: ${rem(20)};

  &:hover {
    text-decoration: underline;
  }
`

const MAX_AMENITIES = 6

interface RoomCardItemProps {
  room: DrawerHotelRoomOption
  deeplinkUrl?: string
}

function RoomCardItem({ room, deeplinkUrl }: RoomCardItemProps) {
  const intl = useIntl()
  const currentCurrency = useContext(CurrencyContext)
  const geo = useContext(GeoContext)
  const firstCapacity = room.capacities[0]

  const packagesUrl = useMemo(() => {
    if (!deeplinkUrl) return undefined
    return addQueryParamsToPath(deeplinkUrl, { roomTypeId: room.roomId })
  }, [deeplinkUrl, room.roomId])

  const visibleAmenities = room.amenities.slice(0, MAX_AMENITIES)
  const carouselImages = useMemo<Array<App.Image>>(
    () => room.imageIds.map((id) => ({ id })),
    [room.imageIds],
  )
  const singleImageId = carouselImages.length === 1 ? carouselImages[0].id : undefined

  let roomImage: React.ReactNode = null
  if (singleImageId) {
    roomImage = <ResponsiveImage id={singleImageId} alt={room.name} fit="center" />
  } else if (carouselImages.length > 1) {
    roomImage = <ImageCarousel images={carouselImages} arrowBehaviour="interaction-visible" />
  }

  const formattedLowestPrice = room.lowestPrice !== undefined
    ? formatRoundedAmountWithDollarType(room.lowestPrice, room.currencyCode ?? currentCurrency, geo.currentRegionCode)
    : '—'

  const packagesLinkText = room.packageCount > 0
    ? intl.formatMessage(
        {
          id: 'exNWvzzbAW-ImBkaeMSy',
          defaultMessage: 'Show {count, plural, one {# package} other {# packages}} from {price}',
          description: 'Link text showing number of available packages and starting price for a room in the hotel drawer',
        },
        { count: room.packageCount, price: formattedLowestPrice },
      )
    : intl.formatMessage({
        id: 'y185Krcwi0DA71rua3rG',
        defaultMessage: 'View packages',
        description: 'Link text to view available packages on the offer page when no pricing is available in the hotel drawer',
      })

  return (
    <RoomCard>
      <RoomContent>
        {roomImage && (
          <RoomImageWrapper>
            {roomImage}
          </RoomImageWrapper>
        )}
        <RoomDetails>
          <Heading variant="heading4">{room.name}</Heading>

          <LabelGroup>
            {room.urgencyTags.map((tag) => (
              <Label key={tag.type} kind="primary" variant="urgency">
                {tag.label}
              </Label>
            ))}
            {firstCapacity && (
              <Label kind="tertiary" variant="ghost" startIcon={<LineUserIcon size="XS" />}>
                {intl.formatMessage(
                  {
                    id: 'g31GXXkjeIsviY6IZREf',
                    defaultMessage: 'Sleeps {count}',
                    description: 'Label showing room capacity in hotel drawer room options',
                  },
                  { count: firstCapacity.adults + firstCapacity.children },
                )}
              </Label>
            )}
            {room.bedGroupDescriptions.map((bed) => (
              <Label key={bed} kind="tertiary" variant="ghost" startIcon={<LineBedIcon size="XS" />}>
                {bed}
              </Label>
            ))}
          </LabelGroup>

          {visibleAmenities.length > 0 && (
            <AmenitiesGrid>
              {visibleAmenities.map((amenity) => (
                <AmenityItem key={amenity.name} amenity={amenity} />
              ))}
            </AmenitiesGrid>
          )}
        </RoomDetails>
      </RoomContent>

      {packagesUrl && (
        <PackagesLink href={packagesUrl} target="_blank" rel="noopener noreferrer">
          {packagesLinkText}
          <LineExternalLinkAltIcon size="S" />
        </PackagesLink>
      )}
    </RoomCard>
  )
}

interface Props {
  roomOptions: Array<DrawerHotelRoomOption>
  deeplinkUrl?: string
}

function HotelDetailRoomOptions({ roomOptions, deeplinkUrl }: Props) {
  const intl = useIntl()

  if (roomOptions.length === 0) return null

  return (
    <Group direction="vertical" gap={12}>
      <Heading variant="heading5">
        {intl.formatMessage({
          id: '3BjVOvp7IcByyR1-eSdv',
          defaultMessage: 'Room options',
          description: 'Heading for the room options section in the hotel detail drawer',
        })}
      </Heading>
      <Group direction="vertical" gap={32}>
        {roomOptions.map((room) => (
          <RoomCardItem
            key={room.roomId}
            room={room}
            deeplinkUrl={deeplinkUrl}
          />
        ))}
      </Group>
    </Group>
  )
}

export default HotelDetailRoomOptions
